import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import React, { useRef, useState, useEffect } from 'react';

import Theme from '../../../themes';

const TabsContainer = styled.div`
  position: relative;
  margin-bottom: 20px;
`;

const FadeOverlay = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 70px;
  pointer-events: none;
  z-index: 2;

  &.left {
    left: 0;
    background: linear-gradient(to right, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
  }

  &.right {
    right: 0;
    background: linear-gradient(to left, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
  }
`;

const TabsBar = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 767px) {
    justify-content: flex-start;
  }

  @media (min-width: 768px) {
    justify-content: center;

    &.has-overflow {
      justify-content: flex-start;
    }
  }
`;

const TabButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: white;
  color: ${props => (props.active ? Theme.colors.brand : '#666')};
  border: none;
  border-bottom: 2px solid
    ${props => {
      if (props.active) return Theme.colors.brand;
      if (props.isCompleted) return Theme.colors.brand;
      return '#e0e0e0';
    }};
  cursor: pointer;
  font-family: ${Theme.fonts.body};
  font-weight: ${props => (props.active ? 'bold' : 'normal')};
  transition: all 0.3s ease;
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: fit-content;

  &:hover {
    background: #f5f5f5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 12px;
  }

  @media (max-width: 767px) {
    padding: 12px 16px;
    gap: 6px;
  }
`;

const StepIndicator = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  transition: all 0.3s ease;

  ${props => {
    if (props.isCompleted && !props.active) {
      return `
        background: ${Theme.colors.brand};
        color: white;
      `;
    }
    if (props.active) {
      return `
        background: ${Theme.colors.brand};
        color: white;
      `;
    }
    return `
      background: #e0e0e0;
      color: #666;
    `;
  }}
`;

const CheckIcon = styled.span`
  font-size: 14px;
  font-weight: bold;
  color: ${Theme.colors.brand};
`;

const TabsNavigation = ({ tabs = [], activeIndex = 0, onSelect }) => {
  const tabsBarRef = useRef(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const truncateText = (text, maxLength = 20) => {
    if (!text || text.length <= maxLength) return text;

    const truncated = text.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');

    if (lastSpaceIndex > maxLength * 0.6) {
      return `${truncated.substring(0, lastSpaceIndex)}...`;
    }

    return `${truncated}...`;
  };

  const checkScrollability = () => {
    if (tabsBarRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsBarRef.current;
      const hasOverflow = scrollWidth > clientWidth;

      setShowScrollButtons(hasOverflow);
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
    }
  };

  useEffect(() => {
    checkScrollability();

    const handleResize = () => checkScrollability();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [tabs]);

  return (
    <TabsContainer>
      {showScrollButtons && canScrollLeft && <FadeOverlay className="left" />}

      <TabsBar
        ref={tabsBarRef}
        role="tablist"
        className={showScrollButtons ? 'has-overflow' : ''}
        onScroll={checkScrollability}
      >
        {tabs.map((tab, idx) => {
          const isCompleted = idx < activeIndex;
          const isActive = activeIndex === idx;

          return (
            <TabButton
              key={tab?.label || idx}
              type="button"
              role="tab"
              active={isActive}
              isCompleted={isCompleted}
              aria-selected={isActive}
              data-active={isActive}
              onClick={() => onSelect(idx)}
            >
              {isCompleted ? (
                <CheckIcon className="oc-icon-check">✓</CheckIcon>
              ) : (
                <StepIndicator isCompleted={isCompleted} active={isActive}>
                  {idx + 1}
                </StepIndicator>
              )}
              {truncateText(tab?.label || `Tab ${idx + 1}`)}
            </TabButton>
          );
        })}
      </TabsBar>

      {showScrollButtons && canScrollRight && <FadeOverlay className="right" />}
    </TabsContainer>
  );
};

TabsNavigation.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
    })
  ),
  activeIndex: PropTypes.number,
  onSelect: PropTypes.func,
};

TabsNavigation.defaultProps = {
  tabs: [],
  activeIndex: 0,
  onSelect: () => {},
};

export default TabsNavigation;
